/**
 * Client-side fuzzy matching utility for autocomplete suggestions.
 * No external dependencies — custom scoring + Levenshtein distance.
 * Performance target: ~5-15ms per keystroke on 5000 questions.
 */

export interface ScoredQuestion {
  questionText: string;
  score: number;
}

function computeCell(
  a: string,
  b: string,
  i: number,
  j: number,
  prevDiag: number,
  prev: number[]
): number {
  return a[i - 1] === b[j - 1]
    ? prevDiag
    : 1 + Math.min(prevDiag, prev[j - 1], prev[j]);
}

function computeRow(a: string, b: string, i: number, prev: number[]): void {
  let prevDiag = prev[0];
  prev[0] = i;
  for (let j = 1; j <= b.length; j++) {
    const temp = prev[j];
    prev[j] = computeCell(a, b, i, j, prevDiag, prev);
    prevDiag = temp;
  }
}

/**
 * Computes the Levenshtein (edit) distance between two strings.
 */
export function levenshteinDistance(a: string, b: string): number {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    computeRow(a, b, i, prev);
  }
  return prev[b.length];
}

function matchShortWord(queryWord: string, questionWords: string[]): boolean {
  return questionWords.some((qw) => qw.startsWith(queryWord));
}

function matchLongWord(queryWord: string, questionWords: string[]): boolean {
  return questionWords.some(
    (qw) =>
      qw === queryWord ||
      qw.startsWith(queryWord) ||
      levenshteinDistance(queryWord, qw) <= 1
  );
}

function wordMatchesAny(queryWord: string, questionWords: string[]): boolean {
  return queryWord.length < 3
    ? matchShortWord(queryWord, questionWords)
    : matchLongWord(queryWord, questionWords);
}

function scoreExactStart(query: string, question: string): number | null {
  return question.startsWith(query) ? 3 : null;
}

function scoreWordBoundary(
  query: string,
  questionWords: string[]
): number | null {
  return questionWords.some((w) => w.startsWith(query)) ? 2 : null;
}

function scoreSubstring(query: string, question: string): number | null {
  return question.includes(query) ? 1 : null;
}

function scoreAllWordsPresent(
  queryWords: string[],
  questionWords: string[]
): number | null {
  if (queryWords.length === 0) return null;
  return queryWords.every((qw) => wordMatchesAny(qw, questionWords))
    ? 0.5
    : null;
}

function scoreByWordContent(
  lowerQuery: string,
  lowerQuestion: string,
  questionWords: string[]
): number {
  const substring = scoreSubstring(lowerQuery, lowerQuestion);
  if (substring !== null) return substring;

  const queryWords = lowerQuery.split(/\s+/).filter((w) => w.length > 0);
  return scoreAllWordsPresent(queryWords, questionWords) ?? 0;
}

function computeScore(lowerQuery: string, lowerQuestion: string): number {
  const questionWords = lowerQuestion.split(/\s+/);

  const exactStart = scoreExactStart(lowerQuery, lowerQuestion);
  if (exactStart !== null) return exactStart;

  const wordBoundary = scoreWordBoundary(lowerQuery, questionWords);
  if (wordBoundary !== null) return wordBoundary;

  return scoreByWordContent(lowerQuery, lowerQuestion, questionWords);
}

/**
 * Scores a question against a query string.
 * Scoring hierarchy: exact-start (3) > word-boundary (2) > substring (1) > all-words-present (0.5)
 */
export function scoreQuestion(query: string, questionText: string): number {
  const lowerQuery = query.toLowerCase().trim();
  if (lowerQuery.length === 0) return 0;
  return computeScore(lowerQuery, questionText.toLowerCase().trim());
}

/**
 * Filters and scores questions against a query, returning the top results.
 * Only returns results with score > 0, sorted descending, capped at limit.
 */
export function fuzzyMatch(
  query: string,
  questions: string[],
  limit = 10
): ScoredQuestion[] {
  const trimmedQuery = query.trim();
  if (trimmedQuery.length === 0) return [];

  const scored: ScoredQuestion[] = [];
  for (const questionText of questions) {
    const score = scoreQuestion(trimmedQuery, questionText);
    if (score > 0) scored.push({ questionText, score });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit);
}
